import React, { useMemo, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import { WizardStepProps } from "@components/WizardCst/types";
import { AssociationItem } from "../../types";
import InitiativeRow from "./components/InitiativeRow";
import { useInitiativesList } from "./hooks/useInitiativesList";
import { InitiativeListItem } from "./types";

const PAGE_SIZE = 10;

interface AssociateInitiativeData {
  selected_initiatives: AssociationItem[];
}

function AssociateInitiativeStep<T extends AssociateInitiativeData>({
  data,
  setData,
}: WizardStepProps<T>): React.ReactElement {
  const { t } = useTranslation("wizard-activity");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");

  const { items, totalItems, totalPages, isLoading } = useInitiativesList({
    corporateId,
    page,
    pageSize,
    search: search || undefined,
  });

  const selectedSet = useMemo(
    () => new Set<string | number>(data.selected_initiatives.map((s) => s.id)),
    [data.selected_initiatives],
  );

  const handleToggle = (id: string | number, next: boolean) => {
    const numericId = typeof id === "string" ? Number(id) : id;
    if (next) {
      const found = items.find((it) => it.id === numericId);
      const label = found?.name || "";
      setData((current) => {
        if (
          current.selected_initiatives.some((s) => Number(s.id) === numericId)
        ) {
          return {} as Partial<T>;
        }
        const item: AssociationItem = { id: numericId, label };
        return {
          selected_initiatives: [...current.selected_initiatives, item],
        } as Partial<T>;
      });
    } else {
      setData(
        (current) =>
          ({
            selected_initiatives: current.selected_initiatives.filter(
              (s) => Number(s.id) !== numericId,
            ),
          }) as Partial<T>,
      );
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box>
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 0.5,
          }}
        >
          {t("associate-initiative.title")}
        </Typography>
        <Typography
          sx={{
            fontSize: "13px",
            color: theme.palette.text.primary,
            lineHeight: 1.5,
          }}
        >
          {t("associate-initiative.description")}
        </Typography>
      </Box>

      <SearchableMultiSelectListCst<InitiativeListItem>
        items={items}
        getId={(initiative) => initiative.id}
        renderRow={({ item, selected, onToggle }) => (
          <InitiativeRow
            initiative={item}
            selected={selected}
            onToggle={onToggle}
          />
        )}
        selectedIds={selectedSet}
        onToggle={handleToggle}
        initialSearchValue={search}
        onSearchSubmit={handleSearch}
        searchPlaceholder={t("associate-initiative.search-placeholder") || ""}
        searchButtonLabel={t("associate-initiative.search-button") || ""}
        page={page}
        pageSize={pageSize}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={(next) => {
          setPageSize(next);
          setPage(1);
        }}
        isLoading={isLoading}
        selectedCountLabel={
          t("associate-initiative.selected-count", {
            count: data.selected_initiatives.length,
          }) || ""
        }
        emptyLabel={t("associate-initiative.empty") || ""}
      />
    </Box>
  );
}

export default AssociateInitiativeStep;
